import { useGA4EventQueue } from '@/composables/useGA4EventQueue'
import { useVisitorTracking } from '@/composables/useVisitorTracking'
import { IS_DEVELOPMENT } from '@/constants/index.js'
import type { FiberStatus } from '@/types/components/signup'

/**
 * Composable for Google Analytics 4 (GA4) tracking
 * Provides methods for tracking various user interactions and events
 * Automatically enriches events with audience properties and page route context
 * @notes
 * - include the tracking location parameter in all events to ensure accurate context (e.g. page or component location)
 * - use the `trackFormEvent` method for universal form element tracking (automatically detects element type and form context)
 * - use the `trackEvent` method for universal non-form element tracking (buttons, links, and other interactive elements)
 * - use the `trackNavigationClick` and `trackNavigationHover` methods for header/footer navigation interactions
 * - use the `trackError` method for error tracking
 * - use the `trackEngagement` method for tracking user engagement with interactive elements
 * - use the `trackFormSubmit` method for form submissions
 * - use the `trackServicePlan` method for service plan interactions
 * - use the `trackAddressCheck` method for address check interactions
 * - use the `trackFiberStatus` method for tracking fiber status determined events
 */
export const useGA4Tracking = () => {
	const { trackGA4Event } = useGA4EventQueue()
	const { getAudienceProperties } = useVisitorTracking()

	// dev client only Logging helpers
	const devLog = {
		warn: (...args: any[]) => {
			if (import.meta.client && IS_DEVELOPMENT) {
				console.warn('[GA4]', ...args)
			}
		}
	}

	// ----------------------------------------------------
	// ------ Base tracking with enriched parameters ------
	// ----------------------------------------------------
	const track = (eventName: string, parameters?: Record<string, any>) => {
		// create enriched parameters with audience properties and current route context
		const audienceProps = getAudienceProperties()
		const routeContext = {
			page_route: window && window.location.href
		}

		const enrichedParams = {
			...parameters,
			...routeContext,
			...audienceProps
		}

		trackGA4Event(eventName, enrichedParams)
	}

	// -----------------------------------------------------------
	// ------ Custom tracking functions for specific events ------
	// -----------------------------------------------------------
	/**
	 * Universal form element tracking
	 * Tracks input, select, and button events with enriched parameters
	 * @param event - The event object
	 * @param trackingLocation - The location where the event occurred (e.g. page or component)
	 * @param parameters - Additional parameters to include in the event
	*/
	const trackFormEvent = (event: Event, trackingLocation: string, parameters?: Record<string, any>) => {
		const target = event.target as HTMLElement
		if (!target) {
			devLog.warn('No event target found for form event tracking')
			return
		}

		// Ensure the target is a valid form element
		if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
			|| target instanceof HTMLSelectElement || target instanceof HTMLButtonElement)) {
			devLog.warn('Event target is not a valid form element:', target.tagName)
			return
		}

		// Ensure the target has an ID or data attribute for tracking
		if (!target.id) {
			devLog.warn('Target element does not have an ID attribute for tracking')
			return
		}

		// Ensure the target has a tracking location
		if (!trackingLocation) {
			devLog.warn('Tracking location is required for form event tracking')
			return
		}

		// Ensure the target has a type attribute if it's an input or button
		if ((target.tagName.toUpperCase() === 'BUTTON' || target.tagName.toUpperCase() === 'INPUT') && !target.getAttribute('type')) {
			devLog.warn('Input element does not have a type attribute for tracking')
			return
		}

		// If the target is a select, ensure it has options
		if (target.tagName.toUpperCase() === 'SELECT' && (target as HTMLSelectElement).options.length === 0) {
			devLog.warn('Select element does not have options for tracking')
			return
		}

		// Find the closest form element to get form ID
		const form = target.form || target.closest('form')
		if (!form) {
			devLog.warn('No form element found for tracking')
			return
		}

		// Determine element type
		const elementType = target.tagName.toLowerCase()
		const inputType = target.getAttribute('type') || 'text'

		// Get event type from the event
		const eventType = event.type

		// Prepare base tracking data
		const trackingData: Record<string, any> = {
			form_id: form.id,
			field_id: target.id,
			element_type: elementType,
			event_type: eventType,
			tracking_location: trackingLocation,
			...parameters
		}

		// Add input-specific attributes for input elements
		if (elementType === 'input' || elementType === 'textarea' || elementType === 'select') {
			trackingData.input_type = inputType

			// Add label text if available (but not input values for privacy)
			const label = target.getAttribute('aria-label')
				|| target.getAttribute('placeholder')
				|| target.getAttribute('label')
				|| form?.querySelector(`label[for="${target.id}"]`)?.textContent?.trim()

			if (label) {
				trackingData.field_label = label
			}
		}

		// Add button-specific attributes for button elements
		if (elementType === 'button' || (elementType === 'input' && [ 'button', 'submit', 'reset' ].includes(inputType))) {
			const buttonText = target.textContent?.trim()
				|| target.getAttribute('aria-label')
				|| target.getAttribute('title')
				|| target.getAttribute('value')
				|| target.getAttribute('label')

			if (buttonText) {
				trackingData.button_text = buttonText
			}

			trackingData.button_type = inputType === 'submit'
				? 'submit'
				: inputType === 'reset' ? 'reset' : 'button'
		}

		// Track the event with appropriate naming based on element type
		if (elementType === 'button' || (elementType === 'input' && [ 'button', 'submit', 'reset' ].includes(inputType))) {
			track(`button_${eventType}`, trackingData)
		} else {
			track(`input_${eventType}`, trackingData)
		}
	}

	/**
	 * Universal event tracking for non-form elements
	 * Tracks button clicks, link clicks, and other element interactions
	 * @param event - The event object
	 * @param trackingLocation - The location where the event occurred (e.g. page or component)
	 * @param parameters - Additional parameters to include in the event
	 */
	const trackEvent = (event: Event, trackingLocation: string, parameters?: Record<string, any>) => {
		const target = event.target as HTMLElement
		if (!target) {
			devLog.warn('No event target found for event tracking')
			return
		}

		// Ensure the target has an ID for tracking
		if (!target.id) {
			devLog.warn('Target element does not have an ID attribute for tracking')
			return
		}

		// Ensure the target has a tracking location
		if (!trackingLocation) {
			devLog.warn('Tracking location is required for event tracking')
			return
		}

		// Get element ID from the target element
		const elementId = target.id

		// Determine element type
		const elementType = target.tagName.toLowerCase()
		const inputType = target.getAttribute('type') || 'button'

		// Get event type from the event
		const eventType = event.type

		// Prepare base tracking data
		const trackingData: Record<string, any> = {
			element_id: elementId,
			element_type: elementType,
			event_type: eventType,
			tracking_location: trackingLocation,
			...parameters
		}

		// Add button-specific attributes for button elements
		if (elementType === 'button' || (elementType === 'input' && [ 'button', 'submit', 'reset' ].includes(inputType))) {
			const buttonText = target.textContent?.trim()
				|| target.getAttribute('aria-label')
				|| target.getAttribute('title')
				|| target.getAttribute('value')
				|| target.getAttribute('label')

			if (buttonText) {
				trackingData.button_text = buttonText
			}

			trackingData.button_type = inputType === 'submit'
				? 'submit'
				: inputType === 'reset' ? 'reset' : 'button'

			track(`button_${eventType}`, trackingData)
			// eslint-disable-next-line @stylistic/brace-style
		}

		// Add link-specific attributes for anchor elements
		else if (elementType === 'a') {
			const linkText = target.textContent?.trim()
				|| target.getAttribute('aria-label')
				|| target.getAttribute('title')

			if (linkText) {
				trackingData.link_text = linkText
			}

			const href = target.getAttribute('href')
			if (href) {
				trackingData.link_url = href
			}

			track(`link_${eventType}`, trackingData)
			// eslint-disable-next-line @stylistic/brace-style
		}
		// Generic element tracking for other elements
		else {
			const elementText = target.textContent?.trim()
				|| target.getAttribute('aria-label')
				|| target.getAttribute('title')

			if (elementText) {
				trackingData.element_text = elementText
			}

			track(`element_${eventType}`, trackingData)
		}
	}

	/**
	 * Track error events
	 * @param message - The error message
	 * @param source - The source of the error (e.g. component name)
	 * @param fatal - Whether the error is fatal
	 */
	const trackError = (message: string, source: string, fatal = false) => {
		track('exception', {
			description: `${source}: ${message}`.substring(0, 150),
			fatal
		})
	}

	/**
	 * Navigation click tracking
	 * Tracks clicks on navigation links in header/footer
	 * @param item - The text of the navigation item clicked
	 * @param location - The location where the click occurred (e.g. header, footer)
	 * @param parameters - Additional parameters to include in the event
	 */
	const trackNavigationClick = (item: string, location: string, parameters?: Record<string, any>) => {
		track('navigation_click', {
			link_text: item,
			link_location: location,
			...parameters
		})
	}

	/**
	 * Navigation hover tracking
	 * Tracks hover interactions on navigation links in header/footer
	 * @param item - The text of the navigation item hovered
	 * @param location - The location where the hover occurred (e.g. header, footer)
	 * @param parameters - Additional parameters to include in the event
	 */
	const trackNavigationHover = (item: string, location: string, parameters?: Record<string, any>) => {
		track('navigation_hover', {
			link_text: item,
			link_location: location,
			...parameters
		})
	}

	/**
	 * Track user engagement with interactive elements
	 * @param action - The engagement action (e.g. click, hover, focus)
	 * @param target - The target element ID or description
	 * @param timeMs - Engagement time in milliseconds
	 * @param parameters - Additional parameters to include in the event
	 */
	const trackEngagement = (action: string, target: string, timeMs: number, parameters?: Record<string, any>) => {
		const params: Record<string, any> = {
			engagement_action: action,
			engagement_target: target
		}

		if (timeMs && timeMs > 0) {
			params.engagement_time_ms = Math.round(timeMs)
		}

		if (parameters) {
			Object.assign(params, parameters)
		}

		track('user_engagement', params)
	}

	// -------------------------------------------------
	// ------ Conversion event tracking functions ------
	// -------------------------------------------------

	/**
	 * Track form submission events
	 * @param formId - The ID of the form being submitted
	 * @param formType - The type of the form (e.g. registration, contact)
	 * @param trackingLocation - The location where the form is submitted (e.g. page or component)
	 * @param parameters - Additional parameters to include in the event
	 */
	const trackFormSubmit = (formId: string, formType: string, trackingLocation: string, parameters?: Record<string, any>) => {
		track('form_submit', {
			form_id: formId,
			form_type: formType,
			tracking_location: trackingLocation,
			...parameters
		})
	}

	// Service plan tracking using standard e-commerce events
	/**
	 * Track service plan events
	 * - Tracks viewing, selecting, and purchasing service plans with standard e-commerce events
	 * @param action - The action being tracked (view, select, purchase)
	 * @param tier - The service tier being interacted with (e.g. 1, 2, 3)
	 * @param speed - The speed of the service plan (e.g. 100, 200, 500)
	 * @param price - The price of the service plan
	 * @param parameters - Additional parameters to include in the event
	 */
	const trackServicePlan = (
		action: 'view' | 'select' | 'purchase',
		tier?: number,
		speed?: number,
		price?: number,
		parameters?: Record<string, any>
	) => {
		const eventMap = {
			view: 'view_item',
			select: 'add_to_cart',
			purchase: 'purchase'
		}

		track(eventMap[action], {
			currency: 'USD',
			value: price,
			items: [ {
				item_id: `tier_${tier}`,
				item_name: `Service Tier ${speed} Mbps`,
				item_category: 'internet_service',
				price: price,
				quantity: 1
			} ],
			...parameters
		})
	}

	/**
	 * Track address check submissionevents such as autocomplete selection or form submission.
	 * @param action - The action being tracked (e.g. 'select', 'submit')
	 * @param zipCode - The ZIP code being checked
	 * @param formId - The ID of the form being submitted
	 * @param parameters - Additional parameters to include in the event
	 */
	const trackAddressCheck = (
		formId: string,
		action: 'select' | 'submit',
		zipCode: string,
		parameters?: Record<string, any>
	) => {
		const eventMap = {
			select: 'select',
			submit: 'inquiry'
		}

		track(eventMap[action], {
			zip_code: zipCode,
			form_id: formId,
			...parameters
		})
	}

	/**
	 * Track fiber status determined events.
	 * @param status - The fiber status (e.g., 'available', 'unavailable', etc.)
	 * @param parameters - Additional parameters to include in the event
	 */
	const trackFiberStatus = (status: FiberStatus, parameters?: Record<string, any>) => {
		track('fiber_status_determined', {
			status,
			...parameters
		})
	}

	return {
		track,
		trackFormEvent,
		trackEvent,
		trackError,
		trackNavigationClick,
		trackNavigationHover,
		trackEngagement,
		trackFormSubmit,
		trackServicePlan,
		trackAddressCheck,
		trackFiberStatus
	}
}
